import { Action, Reducer } from 'redux';
import { fork, call, takeEvery, put, all, select, delay } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import Moment from 'moment';
import { ITableConfig } from '../components/common/table';
import Api from '../api';
import { IRootState } from './index';
import Landman from '../models/Landman';
import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';
import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import Rate from '../models/Rate';
import api from '../api';
import { replace } from 'connected-react-router';
import ManageContactInfo from '../models/forms/ManageContactInfo';
import ManageContactInfoChanges from 'src/models/forms/ManageContactInfoChanges';
import MathUtil from 'src/util/MathUtil';
import Personnel from 'src/models/personnel/Personnel';

// Store
export interface ILandmanState {
    isLandmenLoading: boolean;
    isRatesLoading: boolean;
    isContactInfoLoading: boolean;
    createLandmanLoading: boolean;
    landmen: PagedResultSet<Landman> | undefined;
    rates: PagedResultSet<Rate> | undefined;
    landmanId: number | undefined;
    contactInfo: ManageContactInfo | undefined;
    contactInfoChanges: ManageContactInfoChanges | undefined;
    landmanPageTableConfig: ITableConfig;
    landmanRatePageTableConfig: ITableConfig;
    rate: Rate | undefined;
    selectedPersonnel: Personnel;
}

const initialState: ILandmanState = {
    isLandmenLoading: false,
    isRatesLoading: false,
    isContactInfoLoading: false,
    createLandmanLoading: false,
    landmen: undefined,
    rates: undefined,
    landmanId: undefined,
    contactInfo: undefined,
    contactInfoChanges: undefined,
    rate: undefined,
    landmanPageTableConfig: {
        pageNumber: 1,
        rowCount: 25,
        idField: 'id',
        columns: [
            { name: 'Last Name', mapping: 'lastName' },
            { name: 'First Name', mapping: 'firstName' },
            { name: 'City', mapping: 'primaryAddress.city' },
            { name: 'State', mapping: 'primaryAddress.state.name' },
            { name: 'Phone', mapping: 'primaryPhoneNumber.number' },
            { name: 'Email', mapping: 'username' },
        ],
    },
    landmanRatePageTableConfig: {
        pageNumber: 1,
        rowCount: 5,
        idField: 'id',
        columns: [
            { name: 'Bill Rate', mapping: 'billRate', format: (value: string) => `\$${MathUtil.cleanNumber(parseFloat(value), 5)}` },
            { name: 'Pay Rate', mapping: 'payRate', format: (value: string) => `\$${MathUtil.cleanNumber(parseFloat(value), 5)}` },
            { name: 'Effective Date', mapping: 'effectiveDate', type: 'date', format: (value: string) => Moment(value).format('MM/DD/YYYY') },
        ],
    },
    selectedPersonnel: undefined,
};

// Action Types
export enum actionTypes {
    GET_LANDMEN = '[landman] GET_LANDMEN',
    GET_LANDMEN_SUCCESS = '[landman] GET_LANDMEN_SUCCESS',
    GET_LANDMAN = '[landman] GET_LANDMAN',
    CREATE_LANDMAN = '[landman] CREATE_LANDMAN',
    CREATE_LANDMAN_SUCCESS = '[landman] CREAT_LANDMAN_SUCCESS',
    GET_LANDMAN_SUCCESS = '[landman] GET_LANDMAN_SUCCESS',
    GET_LANDMAN_RATES = '[landman] GET_LANDMAN_RATES',
    GET_LANDMAN_RATES_SUCCESS = '[landman] GET_LANDMAN_RATES_SUCCESS',
    GET_LANDMAN_CONTACTINFO = '[landman] GET_LANDMAN_CONTACTINFO',
    GET_LANDMAN_CONTACTINFO_SUCCESS = '[landman] GET_LANDMAN_CONTACTINFO_SUCCESS',
    UPDATE_LANDMAN_CONTACTINFO = '[landman] UPDATE_LANDMAN_CONTACTINFO',
    ADD_LANDMAN_RATE = '[landman] ADD_LANDMAN_RATE',
    SAVE_LANDMAN_CONTACTINFO = '[landman] SAVE_LANDMAN_CONTACTINFO',
    SAVE_LANDMAN_CONTACTINFO_SUCCESS = '[landman] SAVE_LANDMAN_CONTACTINFO_SUCCESS',
    SAVE_LANDMAN_CONTACTINFO_CHANGES = '[landman] SAVE_LANDMAN_CONTACTINFO_CHANGES',
    SAVE_LANDMAN_CONTACTINFO_CHANGES_SUCCESS = '[landman] SAVE_LANDMAN_CONTACTINFO_CHANGES_SUCCESS',
    SET_SELECTED_PERSONNEL = '[landman] SET_SELECTED_PERSONNEL',
    SET_SELECTED_PERSONNEL_SUCCESS = '[landman] SET_SELECTED_PERSONNEL_SUCCESS'
}

// Action Creator
export const actionCreators = {
    getLandmen: () => {
        return { type: actionTypes.GET_LANDMEN };
    },
    getLandmenSuccess: (landmen: PagedResultSet<Landman>) => {
        return { type: actionTypes.GET_LANDMEN_SUCCESS, payload: landmen };
    },
    getLandman: (id: number) => {
        return { type: actionTypes.GET_LANDMAN, payload: id };
    },
    createLandman: (landman: Landman) => {
        return { type: actionTypes.CREATE_LANDMAN, payload: landman };
    },
    createLandmanSuccess: () => {
        return { type: actionTypes.CREATE_LANDMAN_SUCCESS };
    },
    getLandmanSuccess: (landman: Landman) => {
        return { type: actionTypes.GET_LANDMAN_SUCCESS, payload: landman };
    },
    getLandmanRates: (id: number) => {
        return { type: actionTypes.GET_LANDMAN_RATES, payload: id };
    },
    getLandmanRatesSuccess: (rates: PagedResultSet<Rate>) => {
        return { type: actionTypes.GET_LANDMAN_RATES_SUCCESS, payload: rates };
    },
    addLandmanRate: (rate: Rate) => {
        return { type: actionTypes.ADD_LANDMAN_RATE, payload: rate };
    },
    saveLandmanContactInfo: (values: ManageContactInfo) => {
        return { type: actionTypes.SAVE_LANDMAN_CONTACTINFO, payload: values };
    },
    saveLandmanContactInfoSuccess: (values: ManageContactInfo) => {
        return { type: actionTypes.SAVE_LANDMAN_CONTACTINFO_SUCCESS, payload: values };
    },
    saveLandmanContactInfoChanges: (landmanId: number, formValues: ManageContactInfoChanges) => {
        console.log('on line 124 in Landman Reducer', formValues);
        return { type: actionTypes.SAVE_LANDMAN_CONTACTINFO_CHANGES, payload: { landmanId, formValues } };
    },
    saveLandManContactInfoChangesSuccess: () => {
        return { type: actionTypes.SAVE_LANDMAN_CONTACTINFO_CHANGES_SUCCESS };
    },
    setPersonnel: (personnel: Personnel) => {
        console.log('made it to action creator', personnel);
        return { type: actionTypes.SET_SELECTED_PERSONNEL_SUCCESS, payload: personnel};
    },
};

// Reducer
const reducer: Reducer<ILandmanState> = (state: ILandmanState = initialState, action: Action): ILandmanState => {
    switch (action.type) {
        case actionTypes.GET_LANDMAN:
            return {
                ...state,
                isLandmenLoading: true,
            };
        case actionTypes.GET_LANDMEN:
            return {
                ...state,
                landmen: initialState.landmen,
                isLandmenLoading: true,
            };
        case actionTypes.GET_LANDMAN_SUCCESS:
            const getLandmanPayload = (action as { type: string; payload: Landman }).payload;
            const landmanResultSet = new PagedResultSet<Landman>();
            landmanResultSet.items.push(getLandmanPayload);
            return {
                ...state,
                landmen: landmanResultSet,
                isLandmenLoading: false,
            };
        case actionTypes.GET_LANDMEN_SUCCESS: {
            const payload = (action as { type: string; payload: PagedResultSet<Landman> }).payload;
            return {
                ...state,
                landmen: payload,
                isLandmenLoading: false,
            };
        }
        case actionTypes.CREATE_LANDMAN: {
            return {
                ...state,
                createLandmanLoading: true,
            };
        }
        case actionTypes.CREATE_LANDMAN_SUCCESS: {
            return {
                ...state,
                createLandmanLoading: false,
            };
        }
        case actionTypes.GET_LANDMAN_RATES: {
            return {
                ...state,
                rates: initialState.rates,
                isRatesLoading: true,
            };
        }
        case actionTypes.GET_LANDMAN_RATES_SUCCESS: {
            const payload = (action as { type: string; payload: PagedResultSet<Rate> }).payload;
            return {
                ...state,
                rates: payload,
                isRatesLoading: false,
            };
        }
        case actionTypes.ADD_LANDMAN_RATE: {
            const payload = (action as { type: string; payload: Rate }).payload;
            return {
                ...state,
                isRatesLoading: true,
                rate: payload,
            };
        }
        case actionTypes.SAVE_LANDMAN_CONTACTINFO: {
            return {
                ...state,
                contactInfo: initialState.contactInfo,
                isContactInfoLoading: true,
            };
        }
        case actionTypes.SAVE_LANDMAN_CONTACTINFO_SUCCESS: {
            const payload = (action as { type: string; payload: ManageContactInfo }).payload;
            return {
                ...state,
                contactInfo: payload,
                isContactInfoLoading: false,
            };
        }
        case actionTypes.SAVE_LANDMAN_CONTACTINFO_CHANGES: {
            console.log('SAVE_LANMANCONTACT_INFO_CHANGES_CALLED');
            console.log(state);
            return {
                ...state,
                contactInfoChanges: initialState.contactInfoChanges,
                landmanId: initialState.landmanId,
                isContactInfoLoading: true,
            };
        }
        case actionTypes.SAVE_LANDMAN_CONTACTINFO_CHANGES_SUCCESS: {
            console.log('SAVE_LANMANCONTACT_INFO_CHANGES_SUCCESS_CALLED');
            const payload = (action as { type: string; payload: { landmanId: number; manageContactInfoChanges: ManageContactInfoChanges } }).payload;
            return {
                ...state,
                contactInfoChanges: payload.manageContactInfoChanges,
                landmanId: payload.landmanId,
                isContactInfoLoading: false,
            };
        }
        case actionTypes.SET_SELECTED_PERSONNEL_SUCCESS: {
            const payload = (action as { type: string; payload: Personnel }).payload;
            console.log('made it to the reducer', payload);
            return {
                ...state,
                selectedPersonnel: payload
            };
        }
        default:
            return state;
    }
};

export default reducer;

const landmenSelector = (state: IRootState): PagedResultSet<Landman> | undefined => state.landman.landmen;
const landmanSelector = (id: number) =>
    createSelector(landmenSelector, (landmen) =>
        landmen ? landmen.items.filter((landman: Landman) => landman.id === Number.parseInt(id + '', 10))[0] : undefined
    );

const ratesSelector = (state: IRootState): PagedResultSet<Rate> | undefined => state.landman.rates;

export const selectors = {
    isLandmenLoading: (state: IRootState): boolean => state.landman.isLandmenLoading || state.layout.initialLoading,
    isRatesLoading: (state: IRootState): boolean => state.landman.isLandmenLoading || state.layout.initialLoading,
    isContactInfoLoading: (state: IRootState): boolean => state.landman.isLandmenLoading || state.layout.initialLoading,
    landmen: landmenSelector,
    landman: landmanSelector,
    rates: ratesSelector,
    landmanPageTableConfig: (state: IRootState): ITableConfig => state.landman.landmanPageTableConfig,
    landmanRatePageTableConfig: (state: IRootState): ITableConfig => state.landman.landmanRatePageTableConfig,
    selectedPersonnelId: (state: IRootState): Personnel => state.landman.selectedPersonnel
};

// Side-Effects
const sagas = {
    getLandmen: function* (action: { type: string }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.landmanPageTableConfig);
        const options = ODataOptions.from(tableConfig);
        const [result] = yield all([call(Api.Landman.getAll, options, jwt), delay(200)]);
        yield put(actionCreators.getLandmenSuccess(result));
    },
    getLandman: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const landmanId = action.payload;
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Landman.get, landmanId, jwt), delay(200)]);
        yield put(actionCreators.getLandmanSuccess(result));
    },
    createLandman: function* (action: { type: string; payload: Landman }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(api.Landman.create, action.payload, jwt);
        yield put(actionCreators.createLandmanSuccess());
        yield put(replace(`/landmen/${result.id}`));
    },
    getLandmanRates: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const landmanId = action.payload;
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Landman.getRates, landmanId, jwt), delay(200)]);
        yield put(actionCreators.getLandmanRatesSuccess(result));
    },
    addLandmanRate: function* (action: { type: string; payload: Rate }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const newRate = action.payload;
        yield all([call(Api.Landman.addLandmanRate, newRate, jwt), delay(200)]);
        yield put(actionCreators.getLandmanRates(newRate.userId));
        yield put(actionCreators.getLandman(newRate.userId));
        yield put(layoutActionCreators.hideDialog());
    },
    saveLandmanContactInfo: function* (action: { type: string; payload: ManageContactInfo }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield put(replace(`/landmen/${action.payload.landmanId}`));
        yield all([call(Api.Landman.saveLandmanContactInfo, action.payload, jwt), delay(200)]);
        yield put(actionCreators.getLandman(action.payload.landmanId));
    },
    saveLandmanContactInfoChanges: function* (action: {
        type: string;
        payload: { landmanId: number; formValues: ManageContactInfoChanges };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        console.log('from the reducer saveLandmanContactInfoChanges has been called', action.payload.formValues, action.payload.landmanId);
        yield put(replace(`/landmen/${action.payload.landmanId}`));
        yield all([call(Api.Landman.saveLandmanContactInfoChanges, action.payload.landmanId, action.payload.formValues, jwt), delay(200)]);
        yield put(actionCreators.getLandman(action.payload.landmanId));
    },
    setPersonnel: function* (action: { type: string; payload: Personnel}): unknown {
        console.log('made it to the saga', action.payload);
        yield put(actionCreators.setPersonnel(action.payload));
    },
    
};

const watches = {
    getLandmen: function* () {
        yield takeEvery(actionTypes.GET_LANDMEN, sagas.getLandmen);
    },
    getLandman: function* () {
        yield takeEvery(actionTypes.GET_LANDMAN, sagas.getLandman);
    },
    createLandman: function* () {
        yield takeEvery(actionTypes.CREATE_LANDMAN, sagas.createLandman);
    },
    getLandmanRates: function* () {
        yield takeEvery(actionTypes.GET_LANDMAN_RATES, sagas.getLandmanRates);
    },
    addLandmanRate: function* () {
        yield takeEvery(actionTypes.ADD_LANDMAN_RATE, sagas.addLandmanRate);
    },
    saveLandmanContactInfo: function* () {
        yield takeEvery(actionTypes.SAVE_LANDMAN_CONTACTINFO, sagas.saveLandmanContactInfo);
    },
    saveLandContactInfoChanges: function* () {
        yield takeEvery(actionTypes.SAVE_LANDMAN_CONTACTINFO_CHANGES, sagas.saveLandmanContactInfoChanges);
    },
    setPersonnel: function* () {
        yield takeEvery(actionTypes.SET_SELECTED_PERSONNEL, sagas.setPersonnel);
    }
};

export function* saga() {
    yield fork(watches.getLandmen);
    yield fork(watches.getLandman);
    yield fork(watches.createLandman);
    yield fork(watches.getLandmanRates);
    yield fork(watches.addLandmanRate);
    yield fork(watches.saveLandmanContactInfo);
    yield fork(watches.saveLandContactInfoChanges);
    yield fork(watches.setPersonnel);
}



// WEBPACK FOOTER //
// ./src/reducers/Landman.ts